import type { Address } from '@/types';
import { useBrokerLoanAppProcess } from './useBrokerLoanAppProcess';
import { computed, reactive } from 'vue';

export const hasAddressLine = (addr: Address | undefined): addr is Address => Boolean(addr?.addressLine);

/**
 * Shared registry for "live" addresses from form components.
 * Module-level singleton ensures all composable instances share the same state.
 */
const liveAddressRegistry = reactive(new Map<string, Address>());

/**
 * Deduplicates addresses by comparing addressLine, locality, state, and postcode
 */
export function deduplicateAddresses(addresses: Address[]): Address[] {
  const seen = new Set<string>();
  return addresses.filter((addr) => {
    const key = `${addr.addressLine}|${addr.locality}|${addr.state}|${addr.postcode}`.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Composable that collects all addresses from the current loan application.
 * Used for showing address suggestions in BxAddressInput.
 *
 * Addresses are collected from:
 * - Property addresses (incoming, outgoing, supporting)
 * - Applicant residential addresses
 * - Company/Trust registered addresses
 * - Live addresses registered by form components
 */
export function useApplicationAddresses() {
  const { loanApp } = useBrokerLoanAppProcess();

  /**
   * Register a live address from a form component.
   * Call this when an address field changes to make it available as a suggestion.
   */
  const registerAddress = (key: string, address: Address | undefined) => {
    if (hasAddressLine(address)) {
      liveAddressRegistry.set(key, address);
    } else {
      liveAddressRegistry.delete(key);
    }
  };

  /**
   * Unregister a live address when a form component unmounts.
   */
  const unregisterAddress = (key: string) => {
    liveAddressRegistry.delete(key);
  };

  const applicationAddresses = computed<Address[]>(() => {
    const { incomingProperties, outgoingProperties, supportingProperties, applicants, borrowerGroups } = loanApp.value;

    const propertyAddresses = [
      ...(incomingProperties ?? []),
      ...(outgoingProperties ?? []),
      ...(supportingProperties ?? []),
    ]
      .map((p) => p.address)
      .filter(hasAddressLine);

    const applicantAddresses = (applicants ?? []).map((a) => a.address).filter(hasAddressLine);

    const borrowerAddresses = (borrowerGroups ?? []).flatMap((g) =>
      [g.companyDetails?.registeredAddress, g.trustDetails?.companyDetails?.registeredAddress].filter(hasAddressLine),
    );

    const liveAddresses = Array.from(liveAddressRegistry.values());

    return deduplicateAddresses([...propertyAddresses, ...applicantAddresses, ...borrowerAddresses, ...liveAddresses]);
  });

  return { applicationAddresses, registerAddress, unregisterAddress };
}
